import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/common/Modal';

const PAYBILL_NUMBER = '4039241';

const BUCKET_COLORS = {
  '1-7 days': '#3b82f6',
  '8-30 days': '#f59e0b',
  '31-60 days': '#f97316',
  '61-90 days': '#ef4444',
  '90+ days': '#991b1b',
};

const RATE_COLORS = {
  base: { bg: '#d1fae5', color: '#065f46', label: '14% Base' },
  incrementing: { bg: '#fef3c7', color: '#92400e', label: '15-29%' },
  penalty: { bg: '#fee2e2', color: '#991b1b', label: '30% Cap' },
};

// ═══════════════════════════════════════════════════════════
// INTERACTION FIELD OPTIONS & STYLING
// ═══════════════════════════════════════════════════════════

const METHOD_OPTIONS = [
  { value: 'call', label: '📞 Call', color: '#059669' },
  { value: 'visit', label: '🏠 Visit', color: '#f59e0b' },
  { value: 'sms', label: '💬 SMS', color: '#6366f1' },
  { value: 'whatsapp', label: '🟢 WhatsApp', color: '#25d366' },
  { value: 'chat', label: '🗨️ Chat', color: '#0ea5e9' },
  { value: 'email', label: '📧 Email', color: '#8b5cf6' },
  { value: 'other', label: '📝 Other', color: '#64748b' },
];

const PURPOSE_OPTIONS = [
  'Payment reminder',
  'Customer follow up',
  'Customer reactivation/follow up',
  'Surprise Visit',
  'Spoof calling',
  'Promise to pay',
  'Default management',
  'Collateral list Confirmation',
  'Document collection',
  'Loan application follow up',
  'General inquiry',
  'Complaint resolution',
  'Other',
];

const OUTCOME_OPTIONS = [
  { value: 'Contactable', color: '#059669', bg: '#d1fae5' },
  { value: 'Promise to Pay', color: '#2563eb', bg: '#dbeafe' },
  { value: 'Paid in Full', color: '#059669', bg: '#d1fae5' },
  { value: 'Partial Payment Promised', color: '#d97706', bg: '#fef3c7' },
  { value: 'Said Call Me Later', color: '#d97706', bg: '#fef3c7' },
  { value: 'Visited — Found', color: '#059669', bg: '#d1fae5' },
  { value: 'Voicemail', color: '#64748b', bg: '#f1f5f9' },
  { value: 'Not Contactable', color: '#dc2626', bg: '#fee2e2' },
  { value: 'Wrong Number', color: '#dc2626', bg: '#fee2e2' },
  { value: 'Refused to Pay', color: '#dc2626', bg: '#fee2e2' },
  { value: 'Visited — Not Found', color: '#dc2626', bg: '#fee2e2' },
  { value: 'No Response', color: '#dc2626', bg: '#fee2e2' },
  { value: 'Other', color: '#64748b', bg: '#f1f5f9' },
];

const NEXT_STEPS_OPTIONS = [
  'Call again',
  'Visit again',
  'Send SMS reminder',
  'Send WhatsApp message',
  'Send email',
  'Escalate to manager',
  'Hand over to collections',
  'Close — Resolved',
  'Schedule meeting',
  'Follow up after payment',
  'No further action',
  'Other',
];

const getOutcomeStyle = (outcome) => {
  const found = OUTCOME_OPTIONS.find(o => o.value === outcome);
  return found || { color: '#64748b', bg: '#f1f5f9' };
};

const getMethodIcon = (method) => {
  const found = METHOD_OPTIONS.find(m => m.value === method);
  return found ? found.label.split(' ')[0] : '📝';
};

export default function CollectionDashboard() {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const alertSound = useRef(null);

  // ─── Data States ───
  const [stats, setStats] = useState(null);
  const [overdueLoans, setOverdueLoans] = useState([]);
  const [maturedLoans, setMaturedLoans] = useState([]);
  const [activeLoans, setActiveLoans] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('matured');
  const [showNewlyOverdueAlert, setShowNewlyOverdueAlert] = useState(false);

  // ─── Filters ───
  const [filters, setFilters] = useState({ search: '', bucket: '', rateFilter: '' });

  // ─── Modal States ───
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // ─── Action Modal States ───
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // ─── Forms ───
  const [smsForm, setSmsForm] = useState({ template: 'reminder', message: '' });
  const [interactionForm, setInteractionForm] = useState({
    conversationMethod: 'call',
    conversationPurpose: 'Payment reminder',
    conversationOutcome: 'Contactable',
    content: '',
    nextSteps: 'Call again',
    nextInteractionDate: '',
    promiseAmount: '',
  });
  const [escalateForm, setEscalateForm] = useState({ reason: '' });
  const [writeOffForm, setWriteOffForm] = useState({ reason: '' });
  const [assignForm, setAssignForm] = useState({ officerId: '' });

  // ─── Profile Tab ───
  const [profileTab, setProfileTab] = useState('info');

  useEffect(() => {
    fetchDashboard();
    fetchOfficers();
  }, []);

  useEffect(() => {
    if (stats?.newlyOverdueCount > 0) {
      setShowNewlyOverdueAlert(true);
      try { alertSound.current?.play(); } catch (e) {}
    }
  }, [stats?.newlyOverdueCount]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [statsRes, overdueRes, maturedRes, activeRes, myRes] = await Promise.all([
        api.get('/collections/dashboard').catch(() => null),
        api.get('/collections/overdue?limit=200').catch(() => null),
        api.get('/collections/matured?limit=200&sort=-daysPastMaturity').catch(() => null),
        api.get('/loans?status=active&limit=200').catch(() => null),
        api.get('/collections/my-assignments?limit=200').catch(() => null),
      ]);

      if (statsRes?.data?.data) setStats(statsRes.data.data);
      if (overdueRes?.data?.data) {
        setOverdueLoans(overdueRes.data.data.loans || []);
      } else {
        const fallback = await api.get('/loans?status=overdue&limit=200&sort=-daysOverdue').catch(() => ({ data: { data: { loans: [] } } }));
        setOverdueLoans(fallback.data?.data?.loans || []);
      }
      if (maturedRes?.data?.data) {
        setMaturedLoans(maturedRes.data.data.loans || []);
      }
      setActiveLoans(activeRes?.data?.data?.loans || []);
      setMyAssignments(myRes?.data?.data?.loans || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOfficers = async () => {
    try {
      const res = await api.get('/users?role=Collection+Officer&isActive=true&limit=50');
      setOfficers(res.data?.data?.users || res.data?.data || []);
    } catch (e) {
      try {
        const res = await api.get('/users?isActive=true&limit=100');
        const all = res.data?.data?.users || res.data?.data || [];
        setOfficers(all.filter(u => u.role?.name?.toLowerCase().includes('collection') || u.role?.name?.toLowerCase().includes('officer')));
      } catch (e2) {}
    }
  };

  const fetchClientProfile = async (loan) => {
    setSelectedLoan(loan);
    setProfileTab('info');
    setShowProfileModal(true);
    setProfileLoading(true);
    setClientProfile(null);

    try {
      const res = await api.get(`/collections/client/${loan.client?._id || loan.client}`);
      setClientProfile(res.data.data);
    } catch (err) {
      console.error('Profile fetch error:', err);
      setClientProfile({ client: loan.client, activeLoans: [loan], summary: {}, communications: [] });
    } finally {
      setProfileLoading(false);
    }
  };

  const openActionModal = (loan, type) => {
    setSelectedLoan(loan);
    setActionType(type);
    setSmsForm({ template: 'reminder', message: '' });
    setInteractionForm({
      conversationMethod: 'call',
      conversationPurpose: 'Payment reminder',
      conversationOutcome: 'Contactable',
      content: '',
      nextSteps: 'Call again',
      nextInteractionDate: '',
      promiseAmount: '',
    });
    setEscalateForm({ reason: '' });
    setWriteOffForm({ reason: '' });
    setAssignForm({ officerId: loan.collectionOfficer?._id || loan.collectionOfficer || '' });
    setShowActionModal(true);
  };

  // ═══════════════════════════════════════════════════════════
  // ACTION HANDLERS
  // ═══════════════════════════════════════════════════════════


  const handleSendSms = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await api.post('/collections/send-sms', {
        clientId: selectedLoan.client?._id || selectedLoan.client,
        loanId: selectedLoan._id,
        template: smsForm.template,
        message: smsForm.message || undefined,
      });
      alert('SMS sent successfully!');
      setShowActionModal(false);
    } catch (err) {
      alert(err.response?.data?.message || 'SMS failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogInteraction = async (e) => {
    e.preventDefault();
    if (!interactionForm.content.trim()) return alert('Add details about the interaction');
    setActionLoading(true);
    try {
      const payload = {
        client: selectedLoan.client?._id || selectedLoan.client,
        loan: selectedLoan._id,
        conversationMethod: interactionForm.conversationMethod,
        conversationPurpose: interactionForm.conversationPurpose,
        conversationOutcome: interactionForm.conversationOutcome,
        content: interactionForm.content.trim(),
        nextSteps: interactionForm.nextSteps,
        nextInteractionDate: interactionForm.nextInteractionDate || null,
        direction: 'outbound',
      };

      // If outcome is "Promise to Pay", attach promise details
      if (interactionForm.conversationOutcome === 'Promise to Pay') {
        payload.promiseAmount = interactionForm.promiseAmount || null;
        // nextInteractionDate already serves as the promise date
      }

      await api.post('/collections/interactions', payload);
      alert('Interaction logged successfully!');
      setShowActionModal(false);
      fetchDashboard();
      if (showProfileModal && selectedLoan) {
        setProfileTab('interactions');
        fetchClientProfile(selectedLoan);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to log interaction');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEscalate = async (e) => {
    e.preventDefault();
    if (!escalateForm.reason.trim()) return alert('Provide a reason');
    if (!confirm(`⚠️ ESCALATE loan ${selectedLoan?.loanNumber}?\n\nThis marks the loan as DEFAULTED.`)) return;
    setActionLoading(true);
    try {
      await api.patch(`/collections/${selectedLoan._id}/escalate`, { reason: escalateForm.reason });
      alert('Loan escalated!');
      setShowActionModal(false);
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.message || 'Escalation failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWriteOff = async (e) => {
    e.preventDefault();
    if (!writeOffForm.reason.trim()) return alert('Provide a reason');
    if (!confirm(`⚠️ WRITE OFF loan ${selectedLoan?.loanNumber}?\n\nOutstanding: KES ${selectedLoan?.totalOutstanding?.toLocaleString()}\n\nIRREVERSIBLE.`)) return;
    setActionLoading(true);
    try {
      await api.patch(`/collections/${selectedLoan._id}/write-off`, { reason: writeOffForm.reason });
      alert('Loan written off.');
      setShowActionModal(false);
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.message || 'Write-off failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await api.patch(`/collections/${selectedLoan._id}/assign`, { officerId: assignForm.officerId || null });
      alert('Officer assigned!');
      setShowActionModal(false);
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.message || 'Assignment failed');
    } finally {
      setActionLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // FILTERING
  // ═══════════════════════════════════════════════════════════

  const getDisplayLoans = () => {
    let loans;
    switch (activeTab) {
      case 'matured': loans = maturedLoans; break;
      case 'overdue': loans = overdueLoans; break;
      case 'mine': loans = myAssignments; break;
      case 'active': loans = activeLoans; break;
      default: loans = maturedLoans;
    }

    if (filters.search) {
      const s = filters.search.toLowerCase();
      loans = loans.filter(l =>
        l.client?.firstName?.toLowerCase().includes(s) ||
        l.client?.lastName?.toLowerCase().includes(s) ||
        l.client?.phone?.includes(s) ||
        l.client?.nationalId?.includes(s) ||
        l.client?.clientNumber?.toLowerCase().includes(s) ||
        l.loanNumber?.toLowerCase().includes(s)
      );
    }

    if (filters.bucket && (activeTab === 'overdue' || activeTab === 'matured')) {
      loans = loans.filter(l => {
        const d = l.daysOverdue || 0;
        switch (filters.bucket) {
          case '1-7': return d >= 1 && d <= 7;
          case '8-30': return d >= 8 && d <= 30;
          case '31-60': return d >= 31 && d <= 60;
          case '61-90': return d >= 61 && d <= 90;
          case '90+': return d > 90;
          default: return true;
        }
      });
    }

    if (filters.rateFilter) {
      switch (filters.rateFilter) {
        case 'base': loans = loans.filter(l => l._currentRate <= 14); break;
        case 'incrementing': loans = loans.filter(l => l._currentRate > 14 && l._currentRate < 30); break;
        case 'penalty': loans = loans.filter(l => l._currentRate >= 30); break;
      }
    }

    return loans;
  };

  const getRateBadge = (rate, isPenaltyPhase) => {
    if (isPenaltyPhase) return RATE_COLORS.penalty;
    if (rate <= 14) return RATE_COLORS.base;
    return RATE_COLORS.incrementing;
  };

  if (loading) return <div className="spinner"></div>;

  const displayLoans = getDisplayLoans();
  const isMaturedTab = activeTab === 'matured';

  return (
    <div>
      <audio ref={alertSound} src="/sounds/alert.mp3" preload="none" />

      {/* ═══════════════════════════════════════════════════════════
          NEWLY OVERDUE ALERT BANNER
          ═══════════════════════════════════════════════════════════ */}
      {showNewlyOverdueAlert && stats?.newlyOverdueCount > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
          color: '#fff',
          padding: '16px 20px',
          borderRadius: 10,
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          animation: 'pulse 2s infinite',
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
              🚨 NEW OVERDUE ALERT: {stats.newlyOverdueCount} loan{stats.newlyOverdueCount > 1 ? 's' : ''} just became overdue!
            </div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              {stats.newlyOverdue?.map(l => `${l.loanNumber} (${l.client?.firstName} ${l.client?.lastName}) — ${formatCurrency(l.totalOutstanding)}`).join(' • ')}
            </div>
          </div>
          <button onClick={() => setShowNewlyOverdueAlert(false)} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
            padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 14,
          }}>✕ Dismiss</button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          HEADER & STATS
          ═══════════════════════════════════════════════════════════ */}
      <div className="flex justify-between items-center mb-2">
        <div>
          <h3 style={{ margin: 0 }}>🎯 Collection Officer Dashboard</h3>
          <p className="text-sm text-muted" style={{ margin: '4px 0 0' }}>
            Manage overdue & matured loans, track interactions, enforce collections
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/collections/performance')}>
            📊 Performance
          </button>
          <button className="btn btn-primary btn-sm" onClick={fetchDashboard}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards — 9 cards now including Past Maturity */}
      {stats && (
        <div className="grid gap-1 mb-2" style={{ gridTemplateColumns: 'repeat(9, 1fr)' }}>
          {/* ═══════════════════════════════════════════════════════════
              PAST MATURITY STAT — NEW
              ═══════════════════════════════════════════════════════════ */}
          <div className="card p-2 text-center" style={{ borderLeft: '4px solid #7c3aed', background: '#f5f3ff' }}>
            <div className="text-xs text-muted">Past Maturity</div>
            <div className="font-bold text-lg" style={{ color: '#7c3aed' }}>{stats.maturedCount || 0}</div>
            <div className="text-xs text-muted">{formatCurrency(stats.maturedOutstanding || 0)}</div>
          </div>
          <div className="card p-2 text-center" style={{ borderLeft: '4px solid #dc2626', background: '#fef2f2' }}>
            <div className="text-xs text-muted">Overdue</div>
            <div className="font-bold text-lg" style={{ color: '#dc2626' }}>{stats.overdueCount}</div>
            <div className="text-xs text-muted">{formatCurrency(stats.totalOverdueOutstanding)}</div>
          </div>
          <div className="card p-2 text-center" style={{ borderLeft: '4px solid #059669', background: '#ecfdf5' }}>
            <div className="text-xs text-muted">Active</div>
            <div className="font-bold text-lg" style={{ color: '#059669' }}>{stats.activeLoans}</div>
          </div>
          <div className="card p-2 text-center" style={{ borderLeft: '4px solid #f59e0b', background: '#fffbeb' }}>
            <div className="text-xs text-muted">PAR Ratio</div>
            <div className="font-bold text-lg" style={{ color: stats.parRatio > 20 ? '#dc2626' : stats.parRatio > 10 ? '#f59e0b' : '#059669' }}>
              {stats.parRatio}%
            </div>
          </div>
          <div className="card p-2 text-center" style={{ borderLeft: '4px solid #3b82f6', background: '#eff6ff' }}>
            <div className="text-xs text-muted">Today</div>
            <div className="font-bold text-lg" style={{ color: '#2563eb' }}>{formatCurrency(stats.todayCollected)}</div>
            <div className="text-xs text-muted">{stats.todayCollectionCount} txns</div>
          </div>
          <div className="card p-2 text-center" style={{ borderLeft: '4px solid #8b5cf6', background: '#f5f3ff' }}>
            <div className="text-xs text-muted">This Month</div>
            <div className="font-bold text-lg" style={{ color: '#7c3aed' }}>{formatCurrency(stats.monthCollected)}</div>
            <div className="text-xs text-muted">{stats.monthCollectionCount} txns</div>
          </div>
          <div className="card p-2 text-center" style={{ borderLeft: '4px solid #0ea5e9', background: '#f0f9ff' }}>
            <div className="text-xs text-muted">Avg Rate</div>
            <div className="font-bold text-lg" style={{ color: '#0284c7' }}>{stats.avgCurrentRate}%</div>
            <div className="text-xs text-muted">Current avg</div>
          </div>
          <div className="card p-2 text-center" style={{ borderLeft: '4px solid #dc2626', background: stats.penaltyPhaseCount > 0 ? '#fef2f2' : '#fff' }}>
            <div className="text-xs text-muted">⚠️ 30% Cap</div>
            <div className="font-bold text-lg" style={{ color: stats.penaltyPhaseCount > 0 ? '#dc2626' : '#059669' }}>
              {stats.penaltyPhaseCount}
            </div>
            <div className="text-xs text-muted">{formatCurrency(stats.penaltyPhaseOutstanding)}</div>
          </div>
          <div className="card p-2 text-center" style={{ borderLeft: '4px solid #991b1b', background: '#fef2f2' }}>
            <div className="text-xs text-muted">Defaulted</div>
            <div className="font-bold text-lg" style={{ color: '#991b1b' }}>{stats.defaultedCount}</div>
            <div className="text-xs text-muted">WO: {stats.writtenOffCount}</div>
          </div>
        </div>
      )}

      {/* Aging Buckets Bar */}
      {stats?.buckets && (
        <div className="card p-2 mb-2">
          <h5 style={{ margin: '0 0 10px', fontSize: 14 }}>📊 Aging Analysis</h5>
          <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', height: 40 }}>
            {Object.entries(stats.buckets).map(([key, val]) => {
              const total = stats.overdueCount || 1;
              const width = Math.max((val.count / total) * 100, val.count > 0 ? 5 : 0);
              return (
                <div key={key} style={{
                  width: `${width}%`, background: BUCKET_COLORS[key],
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  minWidth: val.count > 0 ? 60 : 0,
                }}
                  onClick={() => setFilters(prev => ({ ...prev, bucket: prev.bucket === key.replace(/[^0-9+]/g, '') ? '' : key.replace(/[^0-9+]/g, '') }))}
                  title={`${key}: ${val.count} loans, ${formatCurrency(val.amount)}`}
                >
                  {val.count > 0 && `${val.count}`}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            {Object.entries(stats.buckets).map(([key, val]) => (
              <div key={key} style={{ textAlign: 'center', fontSize: 10, color: '#64748b' }}>
                <div style={{ fontWeight: 600, color: BUCKET_COLORS[key] }}>{key}</div>
                <div>{formatCurrency(val.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          TABS — Past Maturity is now first
          ═══════════════════════════════════════════════════════════ */}
      <div className="flex gap-1 mb-2" style={{ borderBottom: '2px solid #e2e8f0' }}>
        {[
          { key: 'matured', label: `💀 Past Maturity (${maturedLoans.length})`, color: '#7c3aed' },
          { key: 'overdue', label: `🔴 Overdue (${overdueLoans.length})`, color: '#dc2626' },
          { key: 'mine', label: `📋 My Assignments (${myAssignments.length})`, color: '#2563eb' },
          { key: 'active', label: `🟢 Active (${activeLoans.length})`, color: '#059669' },
        ].map(tab => (
          <button key={tab.key}
            onClick={() => { setActiveTab(tab.key); setFilters({ search: '', bucket: '', rateFilter: '' }); }}
            style={{
              padding: '10px 16px',
              borderBottom: activeTab === tab.key ? `3px solid ${tab.color}` : '3px solid transparent',
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? tab.color : '#64748b',
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 14,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Past Maturity Info Banner */}
      {isMaturedTab && (
        <div style={{
          background: 'linear-gradient(135deg, #7c3aed20 0%, #a78bfa20 100%)',
          border: '1px solid #a78bfa40',
          padding: '10px 16px',
          borderRadius: 8,
          marginBottom: 12,
          fontSize: 13,
          color: '#5b21b6',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{ fontSize: 20 }}>💀</span>
          <div>
            <strong>Past Maturity Loans</strong> — These loans have passed their maturity date and the <em>full outstanding balance is due immediately</em>.
            {maturedLoans.length > 0 && ` Total exposure: ${formatCurrency(maturedLoans.reduce((s, l) => s + (l.totalOutstanding || 0), 0))}`}
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex gap-2 mb-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="form-control" style={{ maxWidth: 300 }}
          placeholder="🔍 Search by name, ID, phone, loan #..."
          value={filters.search}
          onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
        />
        {(activeTab === 'overdue' || activeTab === 'matured') && (
          <>
            <select className="form-control" style={{ maxWidth: 150 }}
              value={filters.bucket}
              onChange={e => setFilters(prev => ({ ...prev, bucket: e.target.value }))}>
              <option value="">All Ages</option>
              <option value="1-7">1-7 Days</option>
              <option value="8-30">8-30 Days</option>
              <option value="31-60">31-60 Days</option>
              <option value="61-90">61-90 Days</option>
              <option value="90+">90+ Days</option>
            </select>
            <select className="form-control" style={{ maxWidth: 150 }}
              value={filters.rateFilter}
              onChange={e => setFilters(prev => ({ ...prev, rateFilter: e.target.value }))}>
              <option value="">All Rates</option>
              <option value="base">14% Base (Days 1-14)</option>
              <option value="incrementing">15-29% (Days 15-30)</option>
              <option value="penalty">30% Cap (Day 31+)</option>
            </select>
          </>
        )}
        {(filters.search || filters.bucket || filters.rateFilter) && (
          <button className="btn btn-sm btn-outline" onClick={() => setFilters({ search: '', bucket: '', rateFilter: '' })}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          LOANS TABLE
          ═══════════════════════════════════════════════════════════ */}
      <div className="card">
        <div className="table-container">
          <table style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th>Loan #</th>
                <th>Client</th>
                <th>Phone</th>
                <th style={{ textAlign: 'right' }}>Outstanding</th>
                <th style={{ textAlign: 'center' }}>Rate</th>
                <th style={{ textAlign: 'center' }}>
                  {isMaturedTab ? 'Days Past Maturity' : 'Days Overdue'}
                </th>
                {isMaturedTab && <th style={{ textAlign: 'center' }}>Maturity Date</th>}
                <th>Assigned To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayLoans.length === 0 ? (
                <tr><td colSpan={isMaturedTab ? 9 : 8} className="text-center p-4 text-muted">
                  {isMaturedTab ? '🎉 No loans past maturity.' : 'No loans found.'}
                </td></tr>
              ) : displayLoans.map(l => {
                const d = l.daysOverdue || 0;
                let bucketKey = '1-7';
                if (d >= 90) bucketKey = '90+';
                else if (d >= 61) bucketKey = '61-90';
                else if (d >= 31) bucketKey = '31-60';
                else if (d >= 8) bucketKey = '8-30';

                const currentRate = l._currentRate || 14;
                const isPenaltyPhase = l._isPenaltyPhase || false;
                const rateBadge = getRateBadge(currentRate, isPenaltyPhase);
                const daysPastMaturity = l._daysPastMaturity || 0;

                return (
                  <tr key={l._id} style={{
                    background: isMaturedTab ? '#f5f3ff' : isPenaltyPhase ? '#fef2f2' : l.status === 'overdue' ? '#fffbeb' : 'transparent',
                    cursor: 'pointer',
                  }} onClick={() => fetchClientProfile(l)}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--primary)' }}>
                      {l.loanNumber}
                      {isPenaltyPhase && <span style={{ marginLeft: 4, fontSize: 10, color: '#dc2626' }}>⚠️</span>}
                      {isMaturedTab && <span style={{ marginLeft: 4, fontSize: 10, color: '#7c3aed' }}>💀</span>}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{l.client?.firstName} {l.client?.lastName}</div>
                      <div className="text-xs text-muted" style={{ fontFamily: 'monospace' }}>{l.client?.clientNumber}</div>
                    </td>
                    <td>
                      <a href={`tel:${l.client?.phone}`} onClick={e => e.stopPropagation()} style={{ color: '#2563eb', fontSize: 12 }}>
                        {l.client?.phone}
                      </a>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>
                      {formatCurrency(l.totalOutstanding)}
                      <div className="text-xs text-muted" style={{ fontSize: 10 }}>
                        I: {formatCurrency(l.outstandingInterest || 0)} | P: {formatCurrency(l.outstandingPenalty || 0)}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                        background: rateBadge.bg, color: rateBadge.color,
                        border: `1px solid ${rateBadge.color}40`, display: 'inline-block',
                      }}>
                        {currentRate}%
                      </span>
                      <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>
                        Day {l._daysSinceDisbursement || '?'}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                        background: isMaturedTab ? '#7c3aed20' : BUCKET_COLORS[bucketKey] + '20',
                        color: isMaturedTab ? '#7c3aed' : BUCKET_COLORS[bucketKey],
                        border: `1px solid ${isMaturedTab ? '#7c3aed' : BUCKET_COLORS[bucketKey]}`,
                      }}>
                        {isMaturedTab ? `${daysPastMaturity}d` : `${d}d`}
                      </span>
                    </td>
                    {isMaturedTab && (
                      <td style={{ textAlign: 'center', fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>
                        {l.maturityDate ? formatDate(l.maturityDate) : 'N/A'}
                      </td>
                    )}
                    <td className="text-sm" style={{ fontSize: 11 }}>
                      {l.collectionOfficer
                        ? (typeof l.collectionOfficer === 'object'
                          ? `${l.collectionOfficer.firstName || ''} ${l.collectionOfficer.lastName || ''}`
                          : 'Assigned')
                        : <span style={{ color: '#94a3b8' }}>Unassigned</span>
                      }
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1" style={{ flexWrap: 'wrap' }}>
                        <button className="btn btn-sm btn-info" onClick={() => openActionModal(l, 'sms')} title="SMS">📱</button>
                        <button className="btn btn-sm" onClick={() => openActionModal(l, 'interact')} title="Log Interaction"
                          style={{ background: '#7c3aed', color: '#fff', border: 'none' }}>📝</button>
                        {hasPermission('collections', 'update') && (
                          <>
                            <button className="btn btn-sm btn-outline" onClick={() => openActionModal(l, 'assign')} title="Assign">👤</button>
                            {d > 30 && (
                              <button className="btn btn-sm btn-danger" onClick={() => openActionModal(l, 'escalate')} title="Escalate">⚠️</button>
                            )}
                            {d > 90 && hasPermission('loans', 'delete') && (
                              <button className="btn btn-sm btn-danger" onClick={() => openActionModal(l, 'writeoff')} title="Write Off">🗑️</button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          CLIENT PROFILE MODAL
          ═══════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        title={selectedLoan ? `${selectedLoan.client?.firstName} ${selectedLoan.client?.lastName} — Collection Profile` : 'Client Profile'}
        size="1000px"
      >
        {profileLoading ? (
          <div className="text-center p-4"><div className="spinner"></div></div>
        ) : (
          <div>
            <div className="flex gap-1 mb-2" style={{ borderBottom: '2px solid #e2e8f0' }}>
              {[
                { key: 'info', label: '📄 Info' },
                { key: 'interactions', label: `📝 Interactions (${clientProfile?.communications?.length || 0})` },
              ].map(tab => (
                <button key={tab.key} onClick={() => setProfileTab(tab.key)} style={{
                  padding: '8px 16px',
                  borderBottom: profileTab === tab.key ? '3px solid #3b82f6' : '3px solid transparent',
                  fontWeight: profileTab === tab.key ? 600 : 400,
                  color: profileTab === tab.key ? '#1e40af' : '#64748b',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 13,
                }}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── INFO TAB ── */}
            {profileTab === 'info' && clientProfile && (
              <div>
                <div className="grid grid-2 gap-2 mb-2">
                  <div className="card p-2">
                    <h5 style={{ margin: '0 0 8px' }}>👤 Client Details</h5>
                    <div className="text-sm">
                      <p><strong>Name:</strong> {clientProfile.client?.firstName} {clientProfile.client?.lastName}</p>
                      <p><strong>ID:</strong> <span style={{ fontFamily: 'monospace' }}>{clientProfile.client?.nationalId}</span></p>
                      <p><strong>Phone:</strong> <a href={`tel:${clientProfile.client?.phone}`}>{clientProfile.client?.phone}</a></p>
                      <p><strong>Occupation:</strong> {clientProfile.client?.occupation || 'N/A'}</p>
                      <p><strong>Monthly Income:</strong> {clientProfile.client?.monthlyIncome ? formatCurrency(clientProfile.client.monthlyIncome) : 'N/A'}</p>
                    </div>
                  </div>
                  <div className="card p-2">
                    <h5 style={{ margin: '0 0 8px' }}>📊 Collection Summary</h5>
                    <div className="text-sm">
                      <p><strong>Outstanding:</strong> <span style={{ color: '#dc2626', fontWeight: 700 }}>{formatCurrency(clientProfile.summary?.totalOutstanding || 0)}</span></p>
                      <p><strong>Total Paid:</strong> <span style={{ color: '#059669' }}>{formatCurrency(clientProfile.summary?.totalPaid || 0)}</span></p>
                      <p><strong>Active Loans:</strong> {clientProfile.summary?.activeLoanCount || 0}</p>
                      <div style={{ marginTop: 8, padding: 8, background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#0369a1', marginBottom: 4 }}>📈 INTEREST RATE STATUS</div>
                        <p style={{ margin: 0 }}><strong>Avg Current Rate:</strong> {clientProfile.summary?.avgCurrentRate || 0}%</p>
                        <p style={{ margin: 0 }}><strong>Penalty Phase Loans:</strong> <span style={{ color: clientProfile.summary?.penaltyPhaseCount > 0 ? '#dc2626' : '#059669' }}>{clientProfile.summary?.penaltyPhaseCount || 0}</span></p>
                        <p style={{ margin: 0 }}><strong>Total Interest Accrued:</strong> {formatCurrency(clientProfile.summary?.totalInterestAccrued || 0)}</p>
                      </div>
                      <p style={{ marginTop: 8 }}><strong>Paybill:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{PAYBILL_NUMBER}</span></p>
                      <p><strong>Account:</strong> <span style={{ fontFamily: 'monospace' }}>{clientProfile.summary?.accountToUse || clientProfile.client?.nationalId}</span></p>
                    </div>
                  </div>
                </div>

                {clientProfile.activeLoans?.map(loan => {
                  const currentRate = loan._currentRate || 14;
                  const isPenaltyPhase = loan._isPenaltyPhase || false;
                  const rateBadge = getRateBadge(currentRate, isPenaltyPhase);
                  const isMatured = loan._daysPastMaturity > 0;

                  return (
                    <div key={loan._id} className="card p-2 mb-1" style={{
                      borderLeft: `4px solid ${isMatured ? '#7c3aed' : isPenaltyPhase ? '#dc2626' : loan.status === 'overdue' ? '#f59e0b' : '#059669'}`,
                      background: isMatured ? '#f5f3ff' : isPenaltyPhase ? '#fef2f2' : '#fff',
                    }}>
                      <div className="flex justify-between items-center">
                        <div>
                          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)' }}>{loan.loanNumber}</span>
                          <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: rateBadge.bg, color: rateBadge.color, border: `1px solid ${rateBadge.color}40` }}>
                            {currentRate}%
                          </span>
                          {isMatured && (
                            <span style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: '#f5f3ff', color: '#7c3aed', border: '1px solid #a78bfa' }}>
                              💀 {loan._daysPastMaturity}d PAST MATURITY
                            </span>
                          )}
                          {loan.daysOverdue > 0 && !isMatured && (
                            <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 600, fontSize: 12 }}>
                              {loan.daysOverdue}d overdue
                            </span>
                          )}
                          {isPenaltyPhase && (
                            <span style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: '#fee2e2', color: '#dc2626' }}>
                              ⚠️ PENALTY PHASE
                            </span>
                          )}
                        </div>
                        <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 16 }}>{formatCurrency(loan.totalOutstanding)}</div>
                      </div>
                      <div className="text-xs text-muted mt-1" style={{ display: 'flex', gap: 16 }}>
                        <span>Principal: {formatCurrency(loan.principalAmount)}</span>
                        <span>Interest: {formatCurrency(loan._interestAccrued || loan.outstandingInterest)}</span>
                        <span>Penalty: {formatCurrency(loan.outstandingPenalty || 0)}</span>
                        <span>Day {loan._daysSinceDisbursement || '?'}</span>
                        {isMatured && <span style={{ color: '#7c3aed', fontWeight: 600 }}>Maturity: {loan.maturityDate ? formatDate(loan.maturityDate) : 'N/A'}</span>}
                      </div>
                      {loan._rateProgression && (
                        <div style={{ marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 6 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 6 }}>📈 Rate Progression (Last 7 Days)</div>
                          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 50 }}>
                            {loan._rateProgression.map((day, i) => (
                              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{
                                  height: `${(day.rate / 30) * 100}%`,
                                  background: day.rate >= 30 ? '#dc2626' : day.rate > 14 ? '#f59e0b' : '#059669',
                                  borderRadius: '3px 3px 0 0', minHeight: 4, transition: 'height 0.3s',
                                }} />
                                <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>{day.rate}%</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {loan.promiseToPayDate && (
                        <div style={{ marginTop: 4, padding: '4px 8px', background: '#fffbeb', borderRadius: 4, fontSize: 12, border: '1px dashed #f59e0b' }}>
                          🤝 Promise: KES {formatCurrency(loan.promiseToPayAmount || 0)} by {formatDate(loan.promiseToPayDate)}
                        </div>
                      )}
                    </div>
                  );
                })}

                {selectedLoan?.loanApplication?.guarantors?.length > 0 && (
                  <div className="card p-2 mt-2">
                    <h5 style={{ margin: '0 0 8px' }}>👥 Referees / Guarantors</h5>
                    {selectedLoan.loanApplication.guarantors.map((g, i) => (
                      <div key={i} className="p-1 mb-1" style={{ background: '#f8fafc', borderRadius: 4, borderLeft: '3px solid #6366f1', fontSize: 12 }}>
                        <strong>{g.name}</strong> — 📞 <a href={`tel:${g.phone}`}>{g.phone}</a> — {g.relationship}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════
                INTERACTIONS TAB — Enhanced timeline with structured fields
                ═══════════════════════════════════════════════════════════ */}
            {profileTab === 'interactions' && clientProfile && (
              <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                <div style={{ marginBottom: 12 }}>
                  <button className="btn btn-sm" onClick={() => {
                    setShowProfileModal(false);
                    openActionModal(selectedLoan, 'interact');
                  }} style={{ background: '#7c3aed', color: '#fff', border: 'none' }}>
                    ➕ Log Interaction
                  </button>
                </div>

                {(!clientProfile.communications || clientProfile.communications.length === 0) ? (
                  <div className="text-center text-muted p-4">
                    <div style={{ fontSize: 40, marginBottom: 8 }}>📝</div>
                    <div>No interactions recorded yet.</div>
                    <div style={{ fontSize: 12 }}>Click "Log Interaction" to record your first contact attempt.</div>
                  </div>
                ) : (
                  <div style={{ paddingLeft: 12, borderLeft: '2px solid #e5e7eb' }}>
                    {[...(clientProfile.communications || [])].reverse().map((note, i) => {
                      const outcomeStyle = getOutcomeStyle(note.conversationOutcome);
                      const methodIcon = getMethodIcon(note.conversationMethod);
                      const isLegacy = !note.conversationMethod;

                      return (
                        <div key={i} style={{ position: 'relative', paddingLeft: 18, marginBottom: 14 }}>
                          <div style={{
                            position: 'absolute', left: -7, top: 3,
                            width: 12, height: 12, borderRadius: '50%',
                            background: note.conversationMethod === 'sms' ? '#6366f1'
                              : note.conversationMethod === 'call' ? '#059669'
                              : note.conversationMethod === 'visit' ? '#f59e0b'
                              : note.conversationMethod === 'whatsapp' ? '#25d366'
                              : '#64748b',
                            border: '2px solid #fff',
                          }} />
                          <div style={{ fontSize: 12, background: '#f9fafb', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            {/* Header Row */}
                            <div className="flex justify-between items-center mb-1">
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 11 }}>
                                  {isLegacy ? (note.type === 'sms' ? '💬' : note.type === 'call' ? '📞' : note.type === 'visit' ? '🏠' : '📝') : methodIcon}
                                  {' '}{isLegacy ? (note.type || 'note') : note.conversationMethod}
                                </span>
                                {/* Purpose badge */}
                                {note.conversationPurpose && (
                                  <span style={{
                                    padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                    background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
                                  }}>
                                    {note.conversationPurpose}
                                  </span>
                                )}
                                {/* Outcome badge */}
                                {note.conversationOutcome && (
                                  <span style={{
                                    padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                                    background: outcomeStyle.bg, color: outcomeStyle.color,
                                    border: `1px solid ${outcomeStyle.color}40`,
                                  }}>
                                    {note.conversationOutcome}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-muted">{note.createdAt ? new Date(note.createdAt).toLocaleString() : ''}</span>
                            </div>

                            {/* Content */}
                            <p style={{ margin: '4px 0 0', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{note.content}</p>

                            {/* Next Steps & Follow-up */}
                            {(note.nextSteps || note.nextInteractionDate) && (
                              <div style={{ marginTop: 6, padding: '4px 8px', background: '#f0f9ff', borderRadius: 4, border: '1px solid #bae6fd', fontSize: 11 }}>
                                {note.nextSteps && note.nextSteps !== 'No further action' && (
                                  <div><strong>Next:</strong> {note.nextSteps}</div>
                                )}
                                {note.nextInteractionDate && (
                                  <div><strong>Follow-up:</strong> {formatDate(note.nextInteractionDate)}
                                    {new Date(note.nextInteractionDate) < new Date() && (
                                      <span style={{ color: '#dc2626', fontWeight: 700, marginLeft: 6 }}>⏰ OVERDUE</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Legacy promise display */}
                            {note.metadata?.promiseDate && (
                              <div style={{ marginTop: 4, padding: '4px 8px', background: '#fffbeb', borderRadius: 4, border: '1px dashed #f59e0b', fontSize: 11 }}>
                                🤝 Promise: KES {formatCurrency(note.metadata.promiseAmount || 0)} by {formatDate(note.metadata.promiseDate)}
                              </div>
                            )}

                            {/* Officer */}
                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                              By: {note.sentBy?.firstName ? `${note.sentBy.firstName} ${note.sentBy.lastName}` : note.metadata?.officerName || 'System'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </Modal>

      {/* ═══════════════════════════════════════════════════════════
          ACTION MODAL
          ═══════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showActionModal}
        onClose={() => setShowActionModal(false)}
        title={{
          collect: `💵 Collect Payment — ${selectedLoan?.client?.firstName} ${selectedLoan?.client?.lastName}`,
          sms: `📱 Send SMS — ${selectedLoan?.client?.firstName} ${selectedLoan?.client?.lastName}`,
          interact: `📝 Log Interaction — ${selectedLoan?.client?.firstName} ${selectedLoan?.client?.lastName}`,
          escalate: `⚠️ Escalate Loan — ${selectedLoan?.loanNumber}`,
          writeoff: `🗑️ Write Off Loan — ${selectedLoan?.loanNumber}`,
          assign: `👤 Assign Officer — ${selectedLoan?.loanNumber}`,
        }[actionType] || 'Action'}
        size={actionType === 'interact' ? '720px' : '600px'}
      >
        {selectedLoan && (
          <div>
            {/* Loan Summary Bar */}
            <div style={{
              background: selectedLoan._isPenaltyPhase ? '#fef2f2' : selectedLoan._daysPastMaturity > 0 ? '#f5f3ff' : '#f8fafc',
              padding: 12, borderRadius: 8, marginBottom: 16,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13,
              border: selectedLoan._isPenaltyPhase ? '1px solid #fecaca' : selectedLoan._daysPastMaturity > 0 ? '1px solid #a78bfa40' : 'none',
            }}>
              <div>
                <span style={{ fontWeight: 700, color: 'var(--primary)' }}>{selectedLoan.loanNumber}</span>
                <span style={{ marginLeft: 8 }}>{selectedLoan.client?.firstName} {selectedLoan.client?.lastName}</span>
                {selectedLoan._daysPastMaturity > 0 && (
                  <span style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: '#f5f3ff', color: '#7c3aed', border: '1px solid #a78bfa' }}>
                    💀 {selectedLoan._daysPastMaturity}d PAST MATURITY
                  </span>
                )}
                <span style={{
                  marginLeft: 8, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                  background: getRateBadge(selectedLoan._currentRate || 14, selectedLoan._isPenaltyPhase).bg,
                  color: getRateBadge(selectedLoan._currentRate || 14, selectedLoan._isPenaltyPhase).color,
                }}>
                  {selectedLoan._currentRate || 14}%
                </span>
              </div>
              <div>
                <span style={{ color: '#dc2626', fontWeight: 700 }}>{formatCurrency(selectedLoan.totalOutstanding)}</span>
                {selectedLoan.daysOverdue > 0 && <span style={{ marginLeft: 8, color: '#dc2626' }}>({selectedLoan.daysOverdue}d)</span>}
              </div>
            </div>

            {selectedLoan._isPenaltyPhase && (
              <div style={{ background: '#fef2f2', padding: 10, borderRadius: 6, marginBottom: 16, fontSize: 12, color: '#991b1b', border: '1px solid #fecaca' }}>
                <strong>⚠️ PENALTY PHASE:</strong> This loan is at Day {selectedLoan._daysSinceDisbursement} with interest rate capped at <strong>30%</strong>.
                The client is accumulating maximum interest. Prioritize collection.
              </div>
            )}


            {/* ═══════════════════════════════════════════════════════════
                SMS FORM
                ═══════════════════════════════════════════════════════════ */}
            {actionType === 'sms' && (
              <form onSubmit={handleSendSms}>
                <div className="form-group mb-2">
                  <label>Template</label>
                  <select className="form-control" value={smsForm.template}
                    onChange={e => setSmsForm(prev => ({ ...prev, template: e.target.value }))}>
                    <option value="reminder">📄 Payment Reminder</option>
                    <option value="friendly">😊 Friendly Reminder</option>
                    <option value="final_notice">🚨 Final Notice</option>
                    <option value="promise_reminder">🤝 Promise Reminder</option>
                    <option value="penalty_phase">⚠️ Penalty Phase Notice</option>
                    <option value="maturity_notice">💀 Maturity Notice</option>
                    <option value="custom">✏️ Custom Message</option>
                  </select>
                </div>
                {smsForm.template === 'custom' && (
                  <div className="form-group mb-2">
                    <label>Custom Message *</label>
                    <textarea className="form-control" rows={4} required
                      value={smsForm.message}
                      onChange={e => setSmsForm(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Type your message..." />
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
                  📱 To: {selectedLoan.client?.phone}
                </div>
                <button type="submit" className="btn btn-info w-full" disabled={actionLoading}>
                  {actionLoading ? 'Sending...' : '📱 Send SMS'}
                </button>
              </form>
            )}

            {/* ═══════════════════════════════════════════════════════════
                ENHANCED INTERACTION FORM — Structured Fields
                ═══════════════════════════════════════════════════════════ */}
            {actionType === 'interact' && (
              <form onSubmit={handleLogInteraction}>
                {/* ── How did you contact them? ── */}
                <div className="form-group mb-3">
                  <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>
                    📞 How did you contact them?
                  </label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {METHOD_OPTIONS.map(m => (
                      <button key={m.value} type="button"
                        onClick={() => setInteractionForm(prev => ({ ...prev, conversationMethod: m.value }))}
                        style={{
                          padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                          background: interactionForm.conversationMethod === m.value ? m.color : '#f8fafc',
                          color: interactionForm.conversationMethod === m.value ? '#fff' : '#475569',
                          border: `2px solid ${interactionForm.conversationMethod === m.value ? m.color : '#e2e8f0'}`,
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Why are you contacting them? ── */}
                <div className="form-group mb-3">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>🎯 Why are you contacting them?</label>
                  <select className="form-control" value={interactionForm.conversationPurpose}
                    onChange={e => setInteractionForm(prev => ({ ...prev, conversationPurpose: e.target.value }))}>
                    {PURPOSE_OPTIONS.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                {/* ── What was the outcome? ── */}
                <div className="form-group mb-3">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>📋 What was the outcome?</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {OUTCOME_OPTIONS.map(o => (
                      <button key={o.value} type="button"
                        onClick={() => setInteractionForm(prev => ({ ...prev, conversationOutcome: o.value }))}
                        style={{
                          padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                          background: interactionForm.conversationOutcome === o.value ? o.color : o.bg,
                          color: interactionForm.conversationOutcome === o.value ? '#fff' : o.color,
                          border: `2px solid ${interactionForm.conversationOutcome === o.value ? o.color : o.color + '40'}`,
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        {o.value}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Details ── */}
                <div className="form-group mb-3">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>📝 Details *</label>
                  <textarea className="form-control" rows={4} required
                    value={interactionForm.content}
                    onChange={e => setInteractionForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Describe the interaction — what was discussed, client's response, any agreements made..."
                  />
                </div>

                {/* ── Next Steps & Follow-up ── */}
                <div className="grid grid-2 gap-2 mb-3">
                  <div className="form-group">
                    <label style={{ fontWeight: 600, fontSize: 13 }}>➡️ Next Step</label>
                    <select className="form-control" value={interactionForm.nextSteps}
                      onChange={e => setInteractionForm(prev => ({ ...prev, nextSteps: e.target.value }))}>
                      {NEXT_STEPS_OPTIONS.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: 600, fontSize: 13 }}>📅 Follow-up Date</label>
                    <input type="date" className="form-control"
                      value={interactionForm.nextInteractionDate}
                      onChange={e => setInteractionForm(prev => ({ ...prev, nextInteractionDate: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                {/* ── Promise to Pay (conditional) ── */}
                {interactionForm.conversationOutcome === 'Promise to Pay' && (
                  <div style={{
                    background: 'linear-gradient(135deg, #dbeafe 0%, #ede9fe 100%)',
                    border: '2px solid #818cf8',
                    padding: 16, borderRadius: 10, marginBottom: 16,
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#3730a3', marginBottom: 10 }}>
                      🤝 Promise to Pay Details
                    </div>
                    <div className="grid grid-2 gap-2">
                      <div className="form-group">
                        <label>Promise Amount (KES)</label>
                        <input type="number" className="form-control" min="0"
                          value={interactionForm.promiseAmount}
                          onChange={e => setInteractionForm(prev => ({ ...prev, promiseAmount: e.target.value }))}
                          placeholder="e.g. 5000"
                        />
                      </div>
                      <div className="form-group">
                        <label>Promise Date</label>
                        <input type="date" className="form-control"
                          value={interactionForm.nextInteractionDate}
                          onChange={e => setInteractionForm(prev => ({ ...prev, nextInteractionDate: e.target.value }))}
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <div style={{ fontSize: 10, color: '#6366f1', marginTop: 2 }}>
                          Uses the follow-up date above as promise date
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button type="submit" className="btn w-full" disabled={actionLoading}
                  style={{ background: '#7c3aed', color: '#fff', border: 'none', fontWeight: 600 }}>
                  {actionLoading ? 'Saving...' : '✅ Log Interaction'}
                </button>
              </form>
            )}

            {/* ═══════════════════════════════════════════════════════════
                ESCALATE FORM
                ═══════════════════════════════════════════════════════════ */}
            {actionType === 'escalate' && (
              <form onSubmit={handleEscalate}>
                <div style={{ background: '#fef2f2', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 12, color: '#991b1b', border: '1px solid #fecaca' }}>
                  <strong>⚠️ WARNING:</strong> Escalating marks this loan as <strong>DEFAULTED</strong>.
                  Outstanding: <strong>KES {selectedLoan.totalOutstanding?.toLocaleString()}</strong>.
                  This action sends an SMS to the client.
                </div>
                <div className="form-group mb-2">
                  <label>Reason for Escalation *</label>
                  <textarea className="form-control" rows={4} required
                    value={escalateForm.reason}
                    onChange={e => setEscalateForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Explain why this loan is being escalated..." />
                </div>
                <button type="submit" className="btn btn-danger w-full" disabled={actionLoading}>
                  {actionLoading ? 'Escalating...' : '⚠️ Escalate Loan'}
                </button>
              </form>
            )}

            {/* ═══════════════════════════════════════════════════════════
                WRITE OFF FORM
                ═══════════════════════════════════════════════════════════ */}
            {actionType === 'writeoff' && (
              <form onSubmit={handleWriteOff}>
                <div style={{ background: '#fef2f2', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 12, color: '#991b1b', border: '1px solid #fecaca' }}>
                  <strong>🚨 IRREVERSIBLE:</strong> Writing off KES <strong>{selectedLoan.totalOutstanding?.toLocaleString()}</strong>.
                  This loan will be permanently marked as <strong>WRITTEN OFF</strong>.
                </div>
                <div className="form-group mb-2">
                  <label>Reason for Write-Off *</label>
                  <textarea className="form-control" rows={4} required
                    value={writeOffForm.reason}
                    onChange={e => setWriteOffForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Explain why this loan is being written off..." />
                </div>
                <button type="submit" className="btn btn-danger w-full" disabled={actionLoading}>
                  {actionLoading ? 'Processing...' : '🗑️ Write Off Loan'}
                </button>
              </form>
            )}

            {/* ═══════════════════════════════════════════════════════════
                ASSIGN OFFICER FORM
                ═══════════════════════════════════════════════════════════ */}
            {actionType === 'assign' && (
              <form onSubmit={handleAssign}>
                <div className="form-group mb-2">
                  <label>Assign Collection Officer</label>
                  <select className="form-control" value={assignForm.officerId}
                    onChange={e => setAssignForm(prev => ({ ...prev, officerId: e.target.value }))}>
                    <option value="">— Unassign —</option>
                    {officers.map(o => (
                      <option key={o._id} value={o._id}>
                        {o.firstName} {o.lastName} ({o.role?.name || 'Officer'})
                      </option>
                    ))}
                  </select>
                </div>
                <button type="submit" className="btn btn-primary w-full" disabled={actionLoading}>
                  {actionLoading ? 'Assigning...' : '👤 Assign Officer'}
                </button>
              </form>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}